'use client'

import { useState, useRef, useEffect } from 'react'
import { useRouter } from 'next/navigation'
import { Camera, Upload, X, Loader2, ChevronLeft } from 'lucide-react'
import { getSupabaseClient } from '@/utils/supabase/singleton'
import { compressImage, validateImageFile, revokeImagePreview } from '@/utils/imageCompression'
import { certificationLevels } from '@/data/certificationLevels'
import { useTheme } from '@/contexts/ThemeContext'
import { parseAuthToken } from '@/utils/base64'

interface UserProfile {
  id: string
  kakao_id?: string
  naver_id?: string
  name: string
  email: string
  phone: string
  certification_agency: string
  certification_level: string
  region: string
  profile_image: string
  certification_image_url: string
  is_student: boolean
}

export default function ProfileEditPage() {
  const router = useRouter()
  const { theme } = useTheme()
  const isDark = theme === 'dark'
  const [profile, setProfile] = useState<UserProfile | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState('')

  const bg = isDark ? '#0f172a' : '#f4f7fb'
  const card = isDark ? '#1e2a3d' : '#ffffff'
  const border = isDark ? '#2c3a52' : '#e9eff7'
  const ink = isDark ? '#e8f0fb' : '#0a1f3a'
  const secondary = isDark ? '#94a3b8' : '#6b7684'
  const accent = isDark ? '#5ea6ff' : '#2b6df0'

  const inputCls = `w-full px-3 py-2.5 rounded-xl text-sm outline-none transition-all`
  const inputStyle = {
    background: isDark ? '#0f172a' : '#f8fafc',
    border: `1px solid ${border}`,
    color: ink,
  }

  const profileImageRef = useRef<HTMLInputElement>(null)
  const certImageRef = useRef<HTMLInputElement>(null)

  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    is_student: false,
    certification_agency: '',
    certification_level: '',
    region: '',
    profile_image: null as File | null,
    profile_image_url: '',
    certification_image: null as File | null,
    certification_image_url: ''
  })

  useEffect(() => {
    loadProfile()
  }, [])

  const loadProfile = async () => {
    try {
      const token = localStorage.getItem('auth_token')
      if (!token) { router.push('/login'); return }

      const decoded = parseAuthToken(token)
      const supabase = getSupabaseClient()

      let query = supabase.from('user_profiles').select('*')
      if (decoded.kakao_id) {
        query = query.eq('kakao_id', decoded.kakao_id)
      } else if (decoded.naver_id) {
        query = query.eq('naver_id', decoded.naver_id)
      } else {
        router.push('/login')
        return
      }

      const { data, error } = await query.single()
      if (error) { router.push('/profile'); return }

      setProfile(data)
      setFormData({
        name: data.name || '',
        phone: data.phone || '',
        is_student: data.is_student || false,
        certification_agency: data.certification_agency || '',
        certification_level: data.certification_level || '',
        region: data.region || '',
        profile_image: null,
        profile_image_url: data.profile_image || '',
        certification_image: null,
        certification_image_url: data.certification_image_url || ''
      })
    } catch {
      router.push('/profile')
    } finally {
      setIsLoading(false)
    }
  }

  const handleProfileImage = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    const validation = validateImageFile(file)
    if (!validation.valid) { setError(validation.error || '이미지를 처리할 수 없습니다.'); return }
    setError('')
    setIsLoading(true)
    try {
      const compressedFile = await compressImage(file, { maxWidth: 1920, maxHeight: 1920, quality: 0.85, maxSizeMB: 1 })
      if (formData.profile_image_url?.startsWith('blob:')) revokeImagePreview(formData.profile_image_url)
      setFormData({ ...formData, profile_image: compressedFile, profile_image_url: URL.createObjectURL(compressedFile) })
    } catch {
      setError('이미지 처리 중 오류가 발생했습니다.')
    } finally {
      setIsLoading(false)
    }
  }

  const handleCertImage = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    const validation = validateImageFile(file)
    if (!validation.valid) { setError(validation.error || '이미지를 처리할 수 없습니다.'); return }
    setError('')
    setIsLoading(true)
    try {
      const compressedFile = await compressImage(file, { maxWidth: 2400, maxHeight: 2400, quality: 0.9, maxSizeMB: 2 })
      if (formData.certification_image_url?.startsWith('blob:')) revokeImagePreview(formData.certification_image_url)
      setFormData({ ...formData, certification_image: compressedFile, certification_image_url: URL.createObjectURL(compressedFile) })
    } catch {
      setError('이미지 처리 중 오류가 발생했습니다.')
    } finally {
      setIsLoading(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!profile) return
    setIsSaving(true)
    setError('')
    try {
      const supabase = getSupabaseClient()
      let profileImageUrl = formData.profile_image_url
      let certImageUrl = formData.certification_image_url
      const oderId = profile.kakao_id || profile.naver_id || profile.id

      if (formData.profile_image) {
        if (profile.profile_image) {
          const oldFileName = profile.profile_image.split('/').pop()
          if (oldFileName) await supabase.storage.from('profile-images').remove([oldFileName])
        }
        const fileExt = formData.profile_image.name.split('.').pop()
        const fileName = `${oderId}_profile_${Date.now()}.${fileExt}`
        const { error: uploadError } = await supabase.storage.from('profile-images').upload(fileName, formData.profile_image, { upsert: true })
        if (uploadError) throw uploadError
        const { data: { publicUrl } } = supabase.storage.from('profile-images').getPublicUrl(fileName)
        profileImageUrl = publicUrl
      }

      if (!formData.is_student && formData.certification_image) {
        const fileExt = formData.certification_image.name.split('.').pop()
        const fileName = `${oderId}_cert_${Date.now()}.${fileExt}`
        const { error: uploadError } = await supabase.storage.from('certifications').upload(fileName, formData.certification_image, { upsert: true })
        if (uploadError) throw uploadError
        const { data: { publicUrl } } = supabase.storage.from('certifications').getPublicUrl(fileName)
        certImageUrl = publicUrl
      }

      const { error: updateError } = await supabase
        .from('user_profiles')
        .update({
          name: formData.name,
          phone: formData.phone,
          is_student: formData.is_student,
          certification_agency: formData.is_student ? '교육생' : formData.certification_agency,
          certification_level: formData.is_student ? 'student' : formData.certification_level,
          region: formData.region,
          profile_image: profileImageUrl,
          certification_image_url: formData.is_student ? null : certImageUrl,
          profile_completed: true,
          updated_at: new Date().toISOString()
        })
        .eq('id', profile.id)
        .select()
        .single()

      if (updateError) throw updateError
      router.push('/profile')
    } catch (error) {
      setError(error instanceof Error ? error.message : '프로필 수정 중 오류가 발생했습니다.')
    } finally {
      setIsSaving(false)
    }
  }

  if (isLoading) {
    return (
      <div style={{ background: bg, minHeight: '100vh' }} className="flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin" style={{ color: accent }} />
      </div>
    )
  }

  return (
    <div style={{ background: bg, minHeight: '100vh' }}>
      <div className="max-w-2xl mx-auto px-5 py-6 pb-10">
        <div className="flex items-center gap-3 mb-6">
          <button onClick={() => router.push('/profile')} className="w-8 h-8 rounded-xl flex items-center justify-center transition-all"
            style={{ background: card, border: `1px solid ${border}` }}>
            <ChevronLeft className="w-4 h-4" style={{ color: ink }} />
          </button>
          <h1 style={{ font: '700 18px/26px sans-serif', color: ink }}>프로필 수정</h1>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="space-y-4">
            {error && (
              <div className="rounded-xl px-4 py-3" style={{ background: isDark ? 'rgba(239,68,68,0.1)' : '#fef2f2', border: `1px solid ${isDark ? 'rgba(239,68,68,0.2)' : '#fecaca'}` }}>
                <p style={{ font: '400 13px/20px sans-serif', color: '#ef4444' }}>{error}</p>
              </div>
            )}

            <div className="rounded-2xl p-5" style={{ background: card, border: `1px solid ${border}` }}>
              <p style={{ font: '600 13px/20px sans-serif', color: ink, marginBottom: 16 }}>프로필 사진</p>
              <div className="flex items-center gap-4">
                <div onClick={() => profileImageRef.current?.click()}
                  className="w-20 h-20 rounded-full overflow-hidden flex items-center justify-center cursor-pointer transition-opacity hover:opacity-80 flex-shrink-0"
                  style={{ background: isDark ? '#2c3a52' : '#f1f5f9', border: `2px solid ${border}` }}>
                  {formData.profile_image_url ? (
                    <img src={formData.profile_image_url} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <Camera className="w-7 h-7" style={{ color: secondary }} />
                  )}
                </div>
                <input ref={profileImageRef} type="file" accept="image/*" onChange={handleProfileImage} className="hidden" />
                <div>
                  <p style={{ font: '500 13px/20px sans-serif', color: ink }}>사진 변경</p>
                  <p style={{ font: '400 12px/18px sans-serif', color: secondary }}>클릭하여 업로드</p>
                </div>
              </div>
            </div>

            <div className="rounded-2xl p-5 space-y-4" style={{ background: card, border: `1px solid ${border}` }}>
              <p style={{ font: '600 13px/20px sans-serif', color: ink }}>기본 정보</p>
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <label className="block mb-1.5" style={{ font: '500 12px/18px sans-serif', color: secondary }}>이름</label>
                  <input type="text" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} className={inputCls} style={inputStyle} required />
                </div>
                <div>
                  <label className="block mb-1.5" style={{ font: '500 12px/18px sans-serif', color: secondary }}>전화번호</label>
                  <input type="tel" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} className={inputCls} style={inputStyle} required />
                </div>
              </div>
              <div>
                <label className="block mb-1.5" style={{ font: '500 12px/18px sans-serif', color: secondary }}>활동 지역</label>
                <select value={formData.region} onChange={(e) => setFormData({ ...formData, region: e.target.value })} className={inputCls} style={inputStyle} required>
                  <option value="">선택하세요</option>
                  {['서울','경기','인천','강원','충북','충남','대전','세종','전북','전남','광주','경북','경남','대구','울산','부산','제주'].map(r => (
                    <option key={r} value={r}>{r}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="rounded-2xl p-5 space-y-4" style={{ background: card, border: `1px solid ${border}` }}>
              <p style={{ font: '600 13px/20px sans-serif', color: ink }}>자격증 정보</p>
              <div className="flex items-center justify-between py-1">
                <div>
                  <p style={{ font: '500 13px/20px sans-serif', color: ink }}>아직 자격증이 없는 교육생입니다</p>
                  <p style={{ font: '400 12px/18px sans-serif', color: secondary }}>교육 과정 중이거나 취득 예정인 경우</p>
                </div>
                <button type="button" onClick={() => setFormData({ ...formData, is_student: !formData.is_student })}
                  className="relative inline-flex items-center h-6 w-11 rounded-full transition-colors duration-200 flex-shrink-0"
                  style={{ background: formData.is_student ? accent : (isDark ? '#2c3a52' : '#e2e8f0') }}>
                  <span className="inline-block w-4 h-4 rounded-full bg-white shadow transform transition-transform duration-200"
                    style={{ transform: formData.is_student ? 'translateX(24px)' : 'translateX(4px)' }} />
                </button>
              </div>

              {!formData.is_student && (
                <div className="space-y-3">
                  <div className="grid sm:grid-cols-2 gap-3">
                    <div>
                      <label className="block mb-1.5" style={{ font: '500 12px/18px sans-serif', color: secondary }}>자격증 협회</label>
                      <select value={formData.certification_agency} onChange={(e) => setFormData({ ...formData, certification_agency: e.target.value, certification_level: '' })} className={inputCls} style={inputStyle} required>
                        <option value="">선택하세요</option>
                        {['AIDA','PADI','SSI','NAUI','SDI','CMAS','직접 입력'].map(a => <option key={a} value={a}>{a}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block mb-1.5" style={{ font: '500 12px/18px sans-serif', color: secondary }}>자격증 레벨</label>
                      <select value={formData.certification_level} onChange={(e) => setFormData({ ...formData, certification_level: e.target.value })} className={inputCls} style={inputStyle} required>
                        <option value="">선택하세요</option>
                        {(certificationLevels[formData.certification_agency] || certificationLevels.other || []).map((level: any) => (
                          <option key={level.value} value={level.value}>{level.label}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="block mb-1.5" style={{ font: '500 12px/18px sans-serif', color: secondary }}>자격증 사진</label>
                    <div onClick={() => certImageRef.current?.click()}
                      className="w-full rounded-xl p-5 cursor-pointer transition-opacity hover:opacity-80 text-center"
                      style={{ border: `1.5px dashed ${border}`, background: isDark ? '#0f172a' : '#f8fafc' }}>
                      {formData.certification_image_url ? (
                        <img src={formData.certification_image_url} alt="자격증" className="max-w-md mx-auto rounded-lg" />
                      ) : (
                        <div>
                          <Upload className="w-8 h-8 mx-auto mb-2" style={{ color: secondary }} />
                          <p style={{ font: '400 13px/20px sans-serif', color: secondary }}>클릭하여 업로드</p>
                        </div>
                      )}
                    </div>
                    <input ref={certImageRef} type="file" accept="image/*" onChange={handleCertImage} className="hidden" />
                  </div>
                </div>
              )}
            </div>

            <div className="flex gap-3">
              <button type="button" onClick={() => router.push('/profile')} className="flex-1 py-3 rounded-xl font-medium text-sm transition-all"
                style={{ background: card, color: ink, border: `1px solid ${border}` }}>취소</button>
              <button type="submit" disabled={isSaving} className="flex-1 py-3 rounded-xl font-semibold text-sm transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                style={{ background: isDark ? '#1e3a5f' : '#0a1f3a', color: '#fff' }}>
                {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                {isSaving ? '저장 중...' : '저장'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  )
}
